import { useEffect, useRef } from "react";
import { fetchData } from "../service/ganttChartFetchService";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import useDataSelector from "./useDataSelector";
import { useQueryParams } from "./useQueryParams";
import { useSolutionPlanView } from "./useSolutionPlanView";
import { PlanViewStateEntryStatus } from "../store/realTimeUpdates";

/**
 * Custom hook to call backend when either the three query params
 * changes: filterId, date or selectedTab.
 */
export const useFetchOnQueryChange = () => {
  const planViewState = useSolutionPlanView();
  const queryObject = useQueryParams();
  const dispatch = useAppDispatch();
  const { activeSession } = useAppSelector((state) => state.dragAndDropSlice);
  const { selectedGanttChartTab, refetchToken } = useAppSelector(
    (state) => state.ganttChartSlice,
  );
  const { dataToUse } = useDataSelector();
  const prevStatusRef = useRef<PlanViewStateEntryStatus | undefined>(undefined);

  // Read via ref so the effect always sees the latest value without re-running
  // when dataToUse clears. Clearing data (e.g. on date change) must not trigger
  // a fetch — only a queryObject or planViewState change should.
  const dataToUseRef = useRef(dataToUse);
  dataToUseRef.current = dataToUse;

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = planViewState.status;

    // A re-optimization (e.g. contingency, or a plan rerun) finished: the plan
    // state goes loading -> ready while stale data is still loaded. The
    // `!dataToUse` guard below would block the refetch, so force it here.
    const planJustBecameReady =
      planViewState.status === "ready" && prevStatus === "loading";

    const currentDataToUse = dataToUseRef.current;

    if (
      (planViewState.status === "ready" && !currentDataToUse) ||
      planJustBecameReady ||
      selectedGanttChartTab === "Custom"
    ) {
      fetchData(dispatch, planViewState, queryObject, activeSession, currentDataToUse); //Fetches either GanttChartData or sends drag and drop handshake.
    }
  }, [planViewState, queryObject, selectedGanttChartTab, activeSession, dispatch, refetchToken]);
};
